import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionButton, Block, Typography } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { MimeTypes } from '@klp/file';
import type { FileObject } from '@klp/file/classes';
import {
    Form,
    FormButton,
    FormDropdown,
    FormEditor,
    FormFiles,
    FormInput,
    FormValidator,
} from '@klp/form';
import { getDefaultLanguage } from '@klp/layout/Sidebar/config';
import type { User } from '@klp/user/index';

import { SakskategoriType } from '../classes';
import type { CreateSakRequest, ISakInput, Sakskategori } from '../classes';
import {
    useGetSakskategoriListQuery,
    useGetSakskategoriListByTypeQuery,
} from '../rtkApi';

const useStyles = makeStyles((_theme: Theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'column' as const,
        flexGrow: 1,
        '& > *': {
            marginBottom: '1em !important',
            flexBasis: 'auto',
        },
    },
    fileUploader: {
        width: '600px',
    },
}));

interface Props {
    request: CreateSakRequest;
    kundeNummer?: string;
    sakskategoriList?: Array<Sakskategori>;
    sakskategoriListByType?: {
        [key: string]: Array<Sakskategori>;
    };
    disabled?: boolean;
    onSubmit: (event: React.FormEvent, values: ISakInput) => void;
    onCancel: () => void;
    onEmailChangeCallback?: (email: string) => void;
    authenticatedUser?: User;
}

export const SakForm: React.FC<Props> = ({
    request,
    disabled,
    onSubmit,
    onCancel,
    authenticatedUser,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { selectedClient } = useAppParams();

    const [selectedCategory, setSelectedCategory] = useState<
        Sakskategori | undefined
    >(undefined);
    const [selectedBuildingCategory, setSelectedBuildingCategory] = useState<
        Sakskategori | undefined
    >(undefined);
    const [meldingIsEmpty, setMeldingIsEmpty] = useState(true);
    const [emailIsEmpty, setEmailIsEmpty] = useState(
        authenticatedUser?.email === undefined,
    );
    const [emailIsValid, setEmailIsValid] = useState(
        isEmailValidFormat(authenticatedUser?.email ?? ''),
    );
    const [currentEmail, setCurrentEmail] = useState<string | undefined>(
        authenticatedUser?.email,
    );

    function isEmailValidFormat(email?: string): boolean {
        if (email === undefined) {
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    const kundeNummer = selectedClient?.kundeNummer;
    const userEmail = emailIsValid ? currentEmail : '';
    const culture = getDefaultLanguage();

    // Fetch sakskategori list (for building-specific categories)
    const { data: sakskategoriListData } = useGetSakskategoriListQuery(
        {
            kundeNummer: kundeNummer!,
            brukerepost: userEmail,
            culture,
        },
        {
            skip: !kundeNummer || !userEmail,
        },
    );

    // Fetch support categories
    const { data: supportCategoriesData } = useGetSakskategoriListByTypeQuery(
        {
            type: 'support',
            brukerepost: userEmail,
            culture,
        },
        {
            skip: !userEmail,
        },
    );

    // Fetch property categories (when no kundeNummer)
    const { data: propertyCategoriesData } = useGetSakskategoriListByTypeQuery(
        {
            type: 'property',
            brukerepost: userEmail,
            culture,
        },
        {
            skip: !userEmail || !!kundeNummer, // Skip if we have kundeNummer (using sakskategoriList instead)
        },
    );

    const sakskategoriList = sakskategoriListData?.sakskategoriList || [];
    const supportCategories = supportCategoriesData?.sakskategoriList || [];
    const propertyCategories = propertyCategoriesData?.sakskategoriList || [];

    const onSelect = (
        value: string | number,
        categories: Array<Sakskategori>,
        type: SakskategoriType.property | SakskategoriType.support,
    ) => {
        const numericValue =
            typeof value === 'string' ? parseInt(value, 10) : value;
        const category = categories.find((c) => c.id === numericValue);
        if (category) {
            setSelectedCategory(category);
            if (type === SakskategoriType.property) {
                setSelectedBuildingCategory(category);
            }
        }
    };

    const onChange = (value: string) => {
        setMeldingIsEmpty(value.length === 0);
    };

    const onEmailChange = (value: string) => {
        setEmailIsEmpty(
            value.length === 0 && authenticatedUser?.email !== undefined,
        );
        setEmailIsValid(isEmailValidFormat(value));
        setCurrentEmail(value);
    };

    const getCategoriesFiltered = (type: SakskategoriType) => {
        return sakskategoriList.filter((c) => c.type === type);
    };

    const { emne, sakskategoriId, opprettetAv } = request;

    const buttonDisabled =
        meldingIsEmpty ||
        selectedBuildingCategory === undefined ||
        emailIsEmpty ||
        !emailIsValid;

    const buildingCategories =
        kundeNummer && sakskategoriList?.length > 0
            ? getCategoriesFiltered(SakskategoriType.property)
            : propertyCategories;

    // subcategories for property categories
    const subCategories =
        selectedCategory &&
        selectedCategory.children.length > 0 &&
        selectedCategory.type === SakskategoriType.property
            ? selectedCategory.getChildren()
            : undefined;

    const formValues = {
        kundeNummer: selectedClient?.kundeNummer,
        supportkategoriType: SakskategoriType.support,
    };

    const preSelectedCategory =
        buildingCategories?.length === 1 ? buildingCategories[0] : null;

    // Auto-select the category if there's only one
    useEffect(() => {
        if (preSelectedCategory && !selectedCategory) {
            setSelectedCategory(preSelectedCategory);
            setSelectedBuildingCategory(preSelectedCategory);
        }
    }, [preSelectedCategory, selectedCategory]);

    const attachments: Array<FileObject> = [];

    return (
        <Grid container={true}>
            <Form
                className={classes.form}
                onSubmit={onSubmit}
                disabled={disabled}
                values={preSelectedCategory ? { ...formValues } : formValues}
            >
                <Grid item={true} xs={12}>
                    {authenticatedUser?.email ? (
                        <Block paddingTop="compact">
                            <Typography variant="h6" color="inherit">
                                {t('sak.emailCopyRecipient')}{' '}
                                {authenticatedUser?.email}
                            </Typography>
                        </Block>
                    ) : (
                        <FormInput
                            type="email"
                            name="opprettetAv"
                            title={t('sak.email')}
                            value={opprettetAv || ''}
                            onChange={onEmailChange}
                            validators={[
                                new FormValidator(
                                    FormValidator.validations.required,
                                    t('validator.required'),
                                ),
                                new FormValidator(
                                    FormValidator.validations.isEmail,
                                    t('validator.email'),
                                ),
                            ]}
                            required={true}
                        />
                    )}
                </Grid>
                <Grid item={true} xs={12}>
                    <FormInput
                        name="emne"
                        title={t('sak.emne')}
                        value={emne}
                        validators={[
                            new FormValidator(
                                FormValidator.validations.required,
                                t('validator.required'),
                            ),
                        ]}
                        required={true}
                    />
                </Grid>

                {buildingCategories && (
                    <Grid item={true} xs={12} sm={6}>
                        <FormDropdown
                            name="sakskategoriId"
                            title={t('sak.buildingcategory.name')}
                            value={String(
                                preSelectedCategory?.id ?? sakskategoriId ?? '',
                            )}
                            items={buildingCategories.map((item) => {
                                const opt = item.toOption();
                                return {
                                    label: opt.label,
                                    value: String(opt.value),
                                };
                            })}
                            validators={[
                                new FormValidator(
                                    FormValidator.validations.required,
                                    t('validator.required'),
                                ),
                            ]}
                            helperText={t('sak.buildingcategory.helperText')}
                            onChange={(value) =>
                                onSelect(
                                    value,
                                    buildingCategories,
                                    SakskategoriType.property,
                                )
                            }
                            required={true}
                        />
                    </Grid>
                )}

                {subCategories && (
                    <Grid item={true} xs={12} sm={6}>
                        <FormDropdown
                            name="subkategoriId"
                            title={t('sak.subcategory.name')}
                            value=""
                            items={subCategories.map((item) => {
                                const opt = item.toOption();
                                return {
                                    label: opt.label,
                                    value: String(opt.value),
                                };
                            })}
                            helperText={t('sak.subcategory.helperText')}
                        />
                    </Grid>
                )}

                {supportCategories && (
                    <Grid item={true} xs={12} sm={6}>
                        <FormDropdown
                            name="supportkategoriId"
                            title={t('sak.supportcategory.name')}
                            value=""
                            items={supportCategories.map((item) => {
                                const opt = item.toOption();
                                return {
                                    label: opt.label,
                                    value: String(opt.value),
                                };
                            })}
                            helperText={t('sak.supportcategory.helperText')}
                            onChange={(value) =>
                                onSelect(
                                    value,
                                    supportCategories,
                                    SakskategoriType.support,
                                )
                            }
                        />
                    </Grid>
                )}

                <Grid item={true} xs={12}>
                    <FormEditor
                        name="melding"
                        title={t('sak.melding')}
                        validators={[
                            new FormValidator(
                                FormValidator.validations.required,
                                t('validator.required'),
                            ),
                        ]}
                        value=""
                        onChange={onChange}
                        required={true}
                    />
                </Grid>
                <Grid item={true} xs={12}>
                    <div className={classes.fileUploader}>
                        <FormFiles
                            name="sakImagesUploader"
                            title={t('sak.sakImages.name')}
                            value={attachments}
                            mimeTypes={MimeTypes.image}
                            path=""
                            helperText={t('sak.sakImages.helperText')}
                            accept={MimeTypes.image}
                            queueName="sakImages"
                        />
                    </div>
                </Grid>

                <Grid item={true} xs={12}>
                    <Block className={classes.fileUploader} paddingTop="tiny">
                        <FormFiles
                            name="sakFilesUploader"
                            title={t('sak.sakDocuments.name')}
                            value={attachments}
                            mimeTypes={MimeTypes.document}
                            path=""
                            helperText={t('sak.sakDocuments.helperText')}
                            accept={MimeTypes.document}
                            queueName="sakAttachments"
                        />
                    </Block>
                </Grid>

                <Grid item={true} xs={12}>
                    <Grid container={true}>
                        <Grid item={true} xs={12}>
                            <ButtonsContainer
                                marginTop="bulky"
                                justifyContent="flex-end"
                            >
                                <ActionButton
                                    actionType="cancel"
                                    onClick={onCancel}
                                    translation="general.cancel"
                                />

                                <FormButton
                                    name="Submit"
                                    actionType="submit"
                                    disabled={buttonDisabled}
                                    disableOnError={true}
                                    isLoading={disabled}
                                    translation="general.save"
                                />
                            </ButtonsContainer>
                        </Grid>
                    </Grid>
                </Grid>
            </Form>
        </Grid>
    );
};
